import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";

function SortableItem({ id, value, displayValue, onStartEdit, onEdit, onEndEdit, onRemove, isEditing }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <span
      ref={setNodeRef}
      style={style}
      className={`amenity-chip${isDragging ? " amenity-chip--dragging" : ""}`}
    >
      <span
        className="amenity-chip__handle"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical size={13} strokeWidth={2} />
      </span>
      {isEditing ? (
        <input
          type="text"
          className="amenity-chip__input"
          value={displayValue}
          onChange={(e) => onEdit(id, e.target.value)}
          onBlur={onEndEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.target.blur();
          }}
          maxLength={300}
          autoFocus
        />
      ) : (
        <span className="amenity-chip__text" onClick={() => onStartEdit(id)}>
          {value || "(empty)"}
        </span>
      )}
      <button
        type="button"
        className="amenity-chip__remove"
        onClick={() => onRemove(id)}
        aria-label="Remove"
      >
        <X size={12} strokeWidth={2.5} />
      </button>
    </span>
  );
}

export default function AmenitiesList({ amenities = [], onChange }) {
  const [inputValue, setInputValue] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const items = Array.isArray(amenities) ? amenities : [];
  const ids = items.map((_, i) => i);

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onChange([...items, trimmed]);
    setInputValue("");
  };

  const handleRemove = (index) => {
    onChange(items.filter((_, i) => i !== index));
    if (editingId === index) setEditingId(null);
  };

  const handleEditStart = (index, currentValue) => {
    setEditingId(index);
    setEditingValue(currentValue);
  };

  const handleEditChange = (index, value) => {
    setEditingValue(value);
    const next = [...items];
    next[index] = value;
    onChange(next);
  };

  const handleEditEnd = () => {
    if (editingId == null) return;
    const next = [...items];
    next[editingId] = (editingValue || "").trim();
    onChange(next.filter(Boolean));
    setEditingId(null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setEditingId(null);
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(active.id);
      const newIndex = ids.indexOf(over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onChange(arrayMove(items, oldIndex, newIndex));
      }
    }
  };

  return (
    <div className="amenities-add-row-wrap">
      <div className="amenities-add-row">
        <input
          type="text"
          className="form-control"
          placeholder="e.g. WiFi, TV, Mini bar"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAdd())}
          maxLength={300}
        />
        <button type="button" className="amenities-add-btn" onClick={handleAdd}>
          Add
        </button>
      </div>
      {items.length === 0 ? (
        <p className="amenities-empty">No amenities added yet.</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={ids} strategy={rectSortingStrategy}>
            <div className="amenity-chips">
              {items.map((value, index) => (
                <SortableItem
                  key={index}
                  id={index}
                  value={value}
                  displayValue={editingId === index ? editingValue : value}
                  onStartEdit={(id) => handleEditStart(id, items[id])}
                  onEdit={handleEditChange}
                  onEndEdit={handleEditEnd}
                  onRemove={handleRemove}
                  isEditing={editingId === index}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
